import {Component, OnInit, WritableSignal} from "@angular/core";
import {BdcWalkService} from "bdc-walkthrough";
import {MatDialogRef, MatDialogContent} from "@angular/material/dialog";
import {TranslateService, TranslatePipe} from "@ngx-translate/core";
import {TranslatableComponent} from "src/app/core/models/TranslatableComponent";
import {AppearanceService} from "src/app/core/services/services/appearance.service";
import {AuthenticationService} from "../../../core/services/services/authentication.service";
import {EmployeeService} from "../../../core/services/services/employee.service";
import {
	defaultColorSchemes,
	preconfiguredColorSchemes,
} from "../../../core/services/services/appearance-predefined-color-schemes";
import {Browser} from "src/app/core/services/services/appearance-service.d/appearance-helper-browser";
import {Employee} from "../../../core/models/Employee";
import {AlertEnum} from "../../../core/models/Enums/AlertEnum";
import {MessageService} from "../../../core/services/services/message.service";
import {AppearanceHelperWalkthrough} from "../../../core/services/services/appearance-service.d/appearance-helper-walkthrough";
import {LanguageEnum} from "../../../core/models/Enums/LanguageEnum";
import {Preference, PreferencesService} from "../../../core/services/services/preferences.service";
import {CdkDrag, CdkDragHandle} from "@angular/cdk/drag-drop";
import {MatIcon} from "@angular/material/icon";
import {CdkScrollable} from "@angular/cdk/scrolling";
import {NgTemplateOutlet, NgClass} from "@angular/common";
import {MatTooltip} from "@angular/material/tooltip";
import {MatFormField, MatLabel} from "@angular/material/form-field";
import {MatInput} from "@angular/material/input";
import {ReactiveFormsModule, FormsModule} from "@angular/forms";
import {ActiveDirective} from "../../directives/active/active.directive";
import {MatSlideToggle} from "@angular/material/slide-toggle";
import {MatDivider} from "@angular/material/divider";
import {MatSelect} from "@angular/material/select";
import {MatOption} from "@angular/material/autocomplete";
import {EscapeHotkeyDirective} from "../../hotkey/directives/escape-hotkey.directive";
import {LocationService} from "../../../core/services/services/location.service";
import {CountryService} from "../../../core/services/services/country.service";

@Component({
	selector: "app-user-settings",
	templateUrl: "./appearance-dialog.component.html",
	styleUrls: ["./appearance-dialog.component.scss"],
	imports: [
		CdkDrag,
		CdkDragHandle,
		MatIcon,
		CdkScrollable,
		MatDialogContent,
		NgTemplateOutlet,
		NgClass,
		MatTooltip,
		MatFormField,
		MatInput,
		ReactiveFormsModule,
		FormsModule,
		ActiveDirective,
		MatLabel,
		MatSlideToggle,
		MatDivider,
		MatSelect,
		MatOption,
		EscapeHotkeyDirective,
		TranslatePipe,
	],
})
export class AppearanceDialogComponent extends TranslatableComponent implements OnInit {
	protected employeeData: Employee;
	protected isLineManager = false;

	constructor(
		public dialogRef: MatDialogRef<AppearanceDialogComponent>,
		private messageService: MessageService,
		public translate: TranslateService,
		public appearanceService: AppearanceService,
		private authService: AuthenticationService,
		private employeeService: EmployeeService,
		private preferencesService: PreferencesService,
		private locationService: LocationService,
		private countryService: CountryService,
		private bdcWalkService: BdcWalkService,
	) {
		super(translate, "AppearanceDialogComponent");
		this.dialogRef.addPanelClass("mep-dialog");

		if (appearanceService.config.osAccentColor() && appearanceService.likelyBrowser() !== Browser.FIREFOX) {
			appearanceService.config.osAccentColor.set(false);
		}
	}

	async ngOnInit() {
		try {
			const principal = await this.authService.loadPrincipal();
			this.employeeData = principal;
			this.isLineManager = this.authService.isLineManager();
			this.appearanceService.config.emailNotificationPVOutsideArea.set(principal.planningNotification ?? false);
			if (!this.appearanceService.config.language()) {
				this.appearanceService.config.language.set(LanguageEnum.DEFAULT);
			}
			this.selectedLanguage = this.appearanceService.config.language();
		} catch (error) {
			console.error("Fehler beim Laden des eingeloggten Mitarbeiters:", error);
		}
	}

	updatePlanningNotification(checked: boolean) {
		if (!this.employeeData?.id) return;

		this.employeeService.updatePlanningNotification(this.employeeData.id, checked).subscribe({
			next: () => this.messageService.add(this.translate.instant("mep.components.toasts.is-saved"), AlertEnum.SUCCESS),
			error: (err) => console.error("Failed to update notification setting:", err),
		});
	}

	protected readonly preconfiguredColorSchemes = preconfiguredColorSchemes;
	protected readonly Browser = Browser;
	protected readonly Object = Object;
	protected readonly defaultColorSchemes = defaultColorSchemes;
	protected readonly window = window;
	protected readonly AppearanceHelperWalkthrough = AppearanceHelperWalkthrough;
	protected readonly LanguageEnum = LanguageEnum;

	selectedLanguage = LanguageEnum.DEFAULT;
	protected readonly onlanguagechange = onlanguagechange;

	onLanguageChange(): void {
		this.appearanceService.config.language.set(this.selectedLanguage);
		this.preferencesService.put(Preference.LANGUAGE, JSON.stringify(this.selectedLanguage)).then();
		if (this.appearanceService.config.language() === LanguageEnum.DEFAULT) {
			const browserLang = navigator.language.startsWith("de") ? "de" : "en";
			this.translate.use(browserLang);
		} else {
			this.translate.use(this.selectedLanguage);
		}
	}

	resetWalkthrough(walkthrough: {value: WritableSignal<boolean>; taskName?: string}) {
		walkthrough.value.set(false);
		if (walkthrough.taskName) {
			this.bdcWalkService.reset(walkthrough.taskName);
		}
	}
}
